import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { announceToScreenReader } from "@/utils/accessibility";

/**
 * Scrolls to top of page and manages focus on route change
 * Improves accessibility by moving focus to main content and announcing route changes
 */
export function useScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Function to scroll to absolute top (0, 0) without any offset
    const scrollToTop = () => {
      // Use scrollTo with explicit coordinates to ensure we go to absolute top
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto", // Instant scroll, no smooth animation
      });

      // Also set scrollTop directly on document elements for cross-browser compatibility
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
      if (document.body) {
        document.body.scrollTop = 0;
      }
    };

    // Scroll to top immediately
    scrollToTop();

    // Use double requestAnimationFrame to ensure layout is calculated
    // This ensures we scroll after any layout shifts from lazy-loaded content
    let rafId1: number;
    let rafId2: number;

    rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => {
        // Final scroll after layout is calculated
        scrollToTop();
      });
    });

    // Additional scroll after content loads (when Suspense resolves)
    // This ensures we're at the top even after lazy-loaded content renders
    const contentLoadTimeoutId = setTimeout(() => {
      scrollToTop();

      // One more scroll after a longer delay to catch any late-rendering content
      setTimeout(() => {
        scrollToTop();
      }, 300);
    }, 200);

    // Move focus to main content for screen reader users
    // Small delay to ensure DOM is updated
    // Note: focusMainContent uses scrollIntoView which we want to avoid here
    // so we'll just focus without scrolling
    const focusTimeoutId = setTimeout(() => {
      const h1 = document.querySelector("h1");
      const main = document.querySelector("main");
      const target = h1 || main;

      if (target instanceof HTMLElement) {
        // Make sure element is focusable
        if (!target.hasAttribute("tabindex")) {
          target.setAttribute("tabindex", "-1");
        }
        // Focus without scrolling (we already scrolled to top)
        target.focus({ preventScroll: true });
      }

      // Announce route change to screen readers
      const pageTitle = document.title.split("|")[0].trim() || "Page";
      announceToScreenReader(`Navigated to ${pageTitle}`, "polite");
    }, 100);

    // Cleanup on unmount
    return () => {
      cancelAnimationFrame(rafId1);
      if (rafId2) {
        cancelAnimationFrame(rafId2);
      }
      clearTimeout(focusTimeoutId);
      clearTimeout(contentLoadTimeoutId);
    };
  }, [pathname]);
}
